import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI';
import SystemReportTable from './Table/SystemReport';
import { useQuery } from '@tanstack/react-query';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { useSelector } from 'react-redux';
import toasters from '../../../../../../../utils/toasters/Toasters';
import { useSearchParams } from 'react-router-dom'


const formatPercentage = (value, decimals = 2) => {
  return `${(value * 100).toFixed(decimals)}%`;
};

const formatPayloadDates = (payload) => {
  if (!payload) return payload;

  const formattedPayload = { ...payload };
  
  if (formattedPayload.startDate) {
      const startDate = new Date(formattedPayload.startDate);
      if (!isNaN(startDate)) {
          formattedPayload.startDate = startDate.toISOString().split('T')[0];
      }
  }

  if (formattedPayload.endDate) {
      const endDate = new Date(formattedPayload.endDate);
      if (!isNaN(endDate)) {
          formattedPayload.endDate = endDate.toISOString().split('T')[0];
      }
  }

  return formattedPayload;
};

const SystemReport = () => {
  const location = useLocation();
  const { token } = useSelector((state) => state?.auth);

  const [searchParams] = useSearchParams();
  const [payload, setPayload] = useState(null);

  useEffect(() => {

    // Try reading from URL params
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const code = searchParams.get('code');

    if (startDate || endDate) {
      const urlPayload = { startDate, endDate, code };

      setPayload(urlPayload);

      sessionStorage.setItem(
        'systemReportPayload',
        JSON.stringify(urlPayload)
      );

      return;
    }

    // fallback to location.state
    if (location.state) {
      setPayload(location.state);

      sessionStorage.setItem(
        'systemReportPayload',
        JSON.stringify(location.state)
      );

      return;
    }

    // fallback to sessionStorage
    const storedPayload = sessionStorage.getItem('systemReportPayload');

    if (storedPayload) {
      setPayload(JSON.parse(storedPayload));
    }

  }, [location.state, searchParams]);

const formattedPayload = formatPayloadDates(payload);

  const {
    data: SystemReportData,
    isLoading,
  } = useQuery(
    ['fetch-system-report', formattedPayload],
    () => fetchIncidentAnalysisData({ ...formattedPayload, export: 'N' }),
    {
      enabled: !!formattedPayload,
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        return failureCount < 1 && status >= 400 && status < 600;
      },
      retryDelay: 1000, 
    }
  );
  

  const tableColumn = ['Month', 'Users', 'Devices', 'Usage Total', 'Phone Devices', 'Incdt/Month', 'Percent', 'Send to Local'];

  const downloadPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [297, 210]
    });
    doc.setFontSize(18);
    doc.text('System Report', 14, 22);

    doc.setFontSize(12);

    const tableRows = SystemReportData?.map((row) => [
      row?.month || '',
      row?.users || '',
      row?.devices || '',
      row?.usagetotal || '',
      row?.phonedevices || '',
      row?.incidents || '',
      formatPercentage(row?.percent) || '',
      row?.sent_to_local || ''
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows || [],
      startY: 40,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 81, 242],
        textColor: [255, 255, 255],
        fontSize: 12,
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 40 },
        4: { cellWidth: 40 },
        5: { cellWidth: 40 },
        6: { cellWidth: 30 },
        7: { cellWidth: 40 },
      },
    });

    doc.save('system-report.pdf');
  };

  const downloadFile = (data, fileName = 'download') => {
    const csvBlob = new Blob([data], { type: 'text/csv' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const hiddenElement = document.createElement('a');
    hiddenElement.href = csvUrl;
    hiddenElement.target = '_blank';
    hiddenElement.download = `${fileName}.csv`;
    hiddenElement.click();
  };

  const ExcelSystemReports = async () => {
    try {
      let response = await axios.post(
        `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
        { ...formattedPayload, export: 'Y' },
        {
          headers: {
            'Content-type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      downloadFile(response?.data, 'system-report');
    } catch (error) {
      toasters({ type: 'error', message: error?.response?.data?.message });
    }
  };

  return (
    <>
      <SystemReportTable
        isLoading={isLoading}
        SystemReportData={SystemReportData}
        DisplayDate={formattedPayload}
        downloadPDF={downloadPDF}
        downloadExcel={ExcelSystemReports}
        reportData={formattedPayload}
        reportCode={payload?.code || 'systemReport'}
        reportTableData={SystemReportData}
        generatePDFFunction={downloadPDF}
      />
    </>
  );
};

export default SystemReport;
